import { Button, Card, Row, Table } from "antd";
import { useFetchDataCount } from "../../../../../../lib/hooks/useDefaultFetchDataCount";
import { useFetchData } from "../../../../../../lib/hooks/useDefaultFetchData";
import { useGetTableColumns } from "./hooks/useGetTableColumns";
import { TablePagination } from "../../../../../../components/TablePagination";
import { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useOpenSidebar } from "./hooks/useOpenSidebar";
import PayoffQuoteService from "../../../../../../services/accounts/payoffQuote";
import { TPrintData, TRequest } from "./types";
import { IPayoffQuotesGet } from "../../../../../../types/PayoffQute";
import { useDefualtOnRow } from "../../../../../../lib/hooks/useDefaultOnRow";
import { PrintModal } from "./components/PrintModal";
import { IPrimaryData } from "../HeaderActions/types/PrimaryData";
import "./styles.scss";
import { usePermissions } from "../../../../../../lib/hooks/usePermissions";

type TPayoffProps = {
  contractId: string;
  primaryBuyerId: string;
  primarySellerId: string;
};

export const Payoff = ({
  contractId,
  primaryBuyerId,
  primarySellerId,
}: TPayoffProps) => {
  const [id, setId] = useState<string | null>(null);

  const { hasAccessByController } = usePermissions("Payoffs");

  const [itemToPrint, setItemToPrint] = useState<TPrintData>(null);

  const closePrintModal = useCallback(() => setItemToPrint(null), []);

  const primaryData: IPrimaryData = useMemo(
    () => ({
      buyerId: primaryBuyerId,
      sellerId: primarySellerId,
    }),
    [primaryBuyerId, primarySellerId]
  );

  const {
    total,
    request,
    isLoading: isCountLoading,
    onPageChange,
    fetchCount,
  } = useFetchDataCount<{ contractId: string }>(
    PayoffQuoteService,
    { contractId },
    ["contractId"],
    50
  );

  const { data, isLoading, fetchData } = useFetchData<
    TRequest,
    IPayoffQuotesGet
  >(PayoffQuoteService, request, ".payoffs-table .ant-table-content");

  const reloadData = useCallback(() => {
    fetchCount();
    fetchData();
  }, [fetchData, fetchCount]);

  const { formWrapper, isOpen, setIsOpen } = useOpenSidebar({
    id,
    setId,
    reloadData,
    customSidebarClass: "sidebar__container-payoff-quote",
    parentId: contractId,
    primaryBuyerId,
    primarySellerId,
    setItemToPrint,
  });

  const openPayoff = useCallback(
    (id: string) => {
      setId(id);
      setIsOpen(true);
    },
    [setIsOpen]
  );

  const { onRow } = useDefualtOnRow(openPayoff);

  const { columns } = useGetTableColumns({
    setId,
    setIsOpen,
    setItemToPrint,
    reloadData,
    openPayoff,
  });

  const onAddClick = useCallback(() => setIsOpen(true), [setIsOpen]);

  return (
    <Row className="w-100 h-100 py-6 px-8 card-table">
      <Card className="w-100 h-100">
        <Row className="w-100 d-flex justify-space-between">
          <h5 className="h h5 ant-table-title">Payoff Quotes</h5>
          {hasAccessByController("Add") && (
            <Button shape="round" onClick={onAddClick}>
              Add New
            </Button>
          )}
        </Row>
        <Row className="w-100 pt-6 d-block card-table__body">
          <Table
            columns={columns}
            dataSource={data}
            loading={isLoading}
            pagination={false}
            rowKey="id"
            className="w-100 h-100 clickable-rows-table small-table payoffs-table"
            onRow={onRow}
          />
          {total > 0 && (
            <TablePagination
              total={total}
              onPageChange={onPageChange}
              isTotalLoading={isCountLoading}
              currentPage={request.pageNumber}
              pageSize={request.pageSize}
            />
          )}
        </Row>
      </Card>
      <PrintModal
        contractId={contractId}
        itemToPrint={itemToPrint}
        close={closePrintModal}
        primaryData={primaryData}
      />
      {isOpen && createPortal(formWrapper, document.body)}
    </Row>
  );
};
